/**
 * @fileoverview The child's own view (F10), and the parent's switch for it.
 *
 * Two components in one file because they are two halves of one decision.
 *
 * `ChildAccessCard` is what the parent sees on their child's page: two
 * switches, not one, because letting a child READ what their teacher wrote is
 * a different act from letting them PUT something into the product.
 *
 * `ChildModePage` is what the child sees, and it is deliberately the plainest
 * page in the product: their streak, what their teacher asked for, and their
 * lessons. No nav, because there is nowhere else to go — the session it runs
 * in cannot reach anything else, and the page should not imply otherwise.
 *
 * Leaving asks for the parent's password. That is the whole point: without
 * it, this is a button a child presses to reach their parent's account.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import parentApi, { getParentErrorMessage } from "@/lib/parentApi";
import { Card, SectionHeader, Button, ListRow } from "@/components/studio/primitives";
import { SToggle } from "@/components/studio/studentUi";

export function ChildAccessCard({ studentId, firstName }) {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await parentApi.get(`/parent/children/${studentId}/access`);
      setState(data);
    } catch {
      setState(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  // Not eligible means a 13-17 student, who has their own login already —
  // showing a disabled switch would suggest something is missing.
  if (!state || !state.eligible) return null;

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await parentApi.put(`/parent/children/${studentId}/access`, next);
      setState((s) => ({ ...s, access: data.access }));
    } catch (err) {
      toast.error(getParentErrorMessage(err, "Couldn't save that"));
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    try {
      await parentApi.post(`/parent/children/${studentId}/child-mode`);
      navigate("/parent/child");
    } catch (err) {
      toast.error(getParentErrorMessage(err, "Couldn't start child mode"));
    }
  };

  const { access } = state;

  return (
    <Card className="mt-6" data-testid="child-access">
      <SectionHeader>{firstName ? `${firstName}'s own view` : "Their own view"}</SectionHeader>
      <p className="mb-3 font-sans text-[13px] text-[var(--text-tertiary)]">
        A simple page they can use on your phone. They can&rsquo;t sign in on their own, and they can&rsquo;t leave
        this view without your password.
      </p>

      <div className="flex items-center gap-4 py-3">
        <div className="flex-1 font-sans text-[14px] text-[var(--text-primary)]">Let them see their lessons</div>
        <SToggle
          on={access.enabled}
          onToggle={() => save({ enabled: !access.enabled, practice_logging: access.practice_logging })}
          ariaLabel="Child mode"
        />
      </div>

      {access.enabled ? (
        <>
          <div className="flex items-center gap-4 border-t border-[var(--studio-divider)] py-3">
            <div className="flex-1">
              <div className="font-sans text-[14px] text-[var(--text-primary)]">Let them log their practice</div>
              <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
                Just the minutes and the day — nothing else is saved.
              </div>
            </div>
            <SToggle
              on={access.practice_logging}
              onToggle={() => save({ enabled: true, practice_logging: !access.practice_logging })}
              ariaLabel="Practice logging"
            />
          </div>
          <div className="mt-3">
            <Button onPress={start} disabled={busy} data-testid="child-mode-start">
              Open {firstName ? `${firstName}'s` : "their"} view
            </Button>
          </div>
        </>
      ) : null}
    </Card>
  );
}

export function ChildModePage() {
  const navigate = useNavigate();
  const [home, setHome] = useState(null);
  const [exiting, setExiting] = useState(false);
  const [password, setPassword] = useState("");
  const [minutes, setMinutes] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await parentApi.get("/parent/child/home");
      setHome(data);
    } catch {
      setHome(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (home === null) return null;
  if (home === false) {
    return (
      <div className="mx-auto max-w-[520px] px-6 py-16">
        <Card>
          <p className="font-sans text-[15px] text-[var(--text-secondary)]">Ask a grown-up to open this for you.</p>
        </Card>
      </div>
    );
  }

  const logPractice = async () => {
    const value = Number(minutes);
    if (!value) return;
    try {
      await parentApi.post("/parent/child/practice", {
        duration_minutes: value,
        // The child's own local day — the same notion of "day" the streak
        // uses, so the two can never disagree about whether today counts.
        date: new Date().toLocaleDateString("en-CA"),
      });
      setMinutes("");
      toast.success("Nice work!");
      load();
    } catch (err) {
      toast.error(getParentErrorMessage(err, "Couldn't save that"));
    }
  };

  const exit = async () => {
    try {
      await parentApi.post("/parent/child-mode/exit", { password });
      navigate("/parent");
    } catch (err) {
      toast.error(getParentErrorMessage(err, "That's not the password"));
    }
  };

  return (
    <div className="mx-auto max-w-[560px] px-6 py-11" data-testid="child-mode">
      <h1 className="font-serif text-3xl text-[var(--text-primary)]">Hi {home.first_name}!</h1>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-5 text-center">
          <p className="font-mono text-3xl text-[var(--accent-yellow-text)]">{home.streak_days}</p>
          <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">days in a row</p>
        </div>
        <div className="rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-5 text-center">
          <p className="font-mono text-3xl text-[var(--text-primary)]">{Math.round(home.total_minutes / 60)}</p>
          <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">hours practised</p>
        </div>
      </div>

      {home.can_log_practice ? (
        <Card className="mt-5">
          <SectionHeader>Practised today?</SectionHeader>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={600}
              inputMode="numeric"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder="Minutes"
              data-testid="child-minutes"
              className="w-32 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-sm text-[var(--text-primary)]"
            />
            <Button onPress={logPractice} disabled={!minutes} data-testid="child-log-practice">
              Add it
            </Button>
          </div>
        </Card>
      ) : null}

      {home.assignments?.length ? (
        <Card className="mt-5">
          <SectionHeader>To work on</SectionHeader>
          <div className="-mx-[18px]">
            {home.assignments.map((a, i) => (
              <ListRow key={a.assignment_id} borderBottom={i < home.assignments.length - 1} title={a.text} />
            ))}
          </div>
        </Card>
      ) : null}

      {home.lessons?.length ? (
        <Card className="mt-5">
          <SectionHeader>Your lessons</SectionHeader>
          <div className="-mx-[18px]">
            {home.lessons.map((l, i) => (
              <ListRow
                key={l.lesson_id}
                borderBottom={i < home.lessons.length - 1}
                title={l.title || "Lesson"}
                subtitle={l.summary ? l.summary.slice(0, 90) : null}
              />
            ))}
          </div>
        </Card>
      ) : null}

      <div className="mt-8">
        {exiting ? (
          <Card>
            <p className="mb-2 font-sans text-[13px] text-[var(--text-secondary)]">
              A grown-up needs to type their password.
            </p>
            <div className="flex gap-2">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="child-exit-password"
                className="flex-1 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-sm text-[var(--text-primary)]"
              />
              <Button onPress={exit} data-testid="child-exit-confirm">
                Done
              </Button>
            </div>
          </Card>
        ) : (
          <button
            type="button"
            onClick={() => setExiting(true)}
            data-testid="child-exit"
            className="font-sans text-[13px] text-[var(--text-tertiary)] underline"
          >
            Give the phone back
          </button>
        )}
      </div>
    </div>
  );
}

export default ChildModePage;
